import { tenantApi, type MemberRole } from '@ims/remote'
import {
  type Area,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  IconTile,
  Input,
  PageHeader,
  Select,
  TD,
  TH,
  Table,
  Textarea,
  cx,
  useToast,
  SkeletonForm,
  SkeletonTable,
  Switch,
} from '@ims/ui'
import { keepTyped, readAmountText } from '@ims/core'
import {
  BarChart3,
  Bell,
  BookOpenText,
  CashRegister,
  ChevronLeft,
  ChevronRight,
  Copy,
  CreditCard,
  type IconType,
  Mail,
  MapPin,
  MonitorSmartphone,
  Package,
  Percent,
  PicnicTable,
  Plus,
  QrCode,
  Ruler,
  Stamp,
  Smartphone,
  Store,
  UserPlus,
  Users,
} from '@ims/ui/icons'
import { useCallback, useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { AlertSettings } from './alert-settings.tsx'
import { DayReportSettings } from './day-report-settings.tsx'
import { PaymentSettings } from './payment-settings.tsx'
import { SECTION_ACTIVE } from './section-active.ts'
import { LogoPart, useLogoChoice, type LogoChoice } from './logo-settings.tsx'
import { SaveBar, SettingsPart } from './settings-part.tsx'
import { KindsParts } from './business-kinds-settings.tsx'
import { businessKinds, providesServices } from '../lib/catalog.ts'
import { SubscriptionSettings } from './billing.tsx'
import { CatalogSettings } from './catalog-settings.tsx'
import { TableSettings } from './restaurant.tsx'
import { UnitSettings } from './units.tsx'
import { useLookups } from '../components.tsx'
import { errorMessage, useFeature, useLive, useOnline, useSettings } from '../hooks.ts'
import { createApi } from '../lib/api.ts'
import { businessDateDisplay } from '../lib/calendar.ts'
import { dateLabel, todayIso } from '../lib/format.ts'
import { taxNumberLabel } from '../lib/registration.ts'
import { insertOp, newLocation, newTaxRate, updateOp } from '../lib/rows.ts'
import { useUnsavedWarning } from '../lib/unsaved.ts'
import { useSession, useUser } from '../session.tsx'
import { DeviceSettings } from '../layout/storage.tsx'
import { roleAtLeast, useCan, useRepo, useRole, useStore, useWorkspace } from '../workspace.tsx'
import { PurchasingSettings } from './purchasing-settings.tsx'

type SectionId =
  | 'business'
  | 'locations'
  | 'team'
  | 'day-report'
  | 'subscription'
  | 'taxes'
  | 'payments'
  | 'tables'
  | 'counter'
  | 'products'
  | 'units'
  | 'purchasing'
  | 'alerts'
  | 'device'

interface Section {
  id: SectionId
  label: string
  area: Area
  icon: IconType
  /** Under the section's heading. */
  about: string
}

// The ids are part of the address (`/settings?tab=<id>`), which emails, the payment
// return and many links in the app use: never rename one.
const GROUPS: { label: string; shopLabel?: string; sections: Section[] }[] = [
  {
    label: 'Business',
    sections: [
      {
        id: 'business',
        label: 'Business',
        area: 'sell',
        icon: Store,
        about: 'Who you are on a bill, how bills are numbered and how dates read.',
      },
      {
        id: 'locations',
        label: 'Locations',
        area: 'stock',
        icon: MapPin,
        about: 'The places that hold your stock.',
      },
      {
        id: 'team',
        label: 'Team',
        area: 'people',
        icon: Users,
        about: 'Who can open this business and what each person may do.',
      },
      {
        id: 'day-report',
        label: 'Day report',
        area: 'insight',
        icon: BarChart3,
        about: 'One email after each day with sales: the total, what to check, and the money.',
      },
      {
        id: 'subscription',
        label: 'Subscription',
        area: 'insight',
        icon: CreditCard,
        about: 'Your plan and payments.',
      },
    ],
  },
  {
    label: 'Selling',
    sections: [
      {
        id: 'taxes',
        label: 'Tax rates',
        area: 'sell',
        icon: Percent,
        about: 'The VAT and other rates a product can carry.',
      },
      {
        id: 'payments',
        label: 'Payments',
        area: 'sell',
        icon: QrCode,
        about: 'The QR your customers scan to pay you.',
      },
      {
        id: 'tables',
        label: 'Tables',
        area: 'kitchen',
        icon: PicnicTable,
        about: 'Your tables, kitchen tickets and QR codes.',
      },
      {
        id: 'counter',
        label: 'Billing counter',
        area: 'sell',
        icon: CashRegister,
        about: 'Devices that number their own bills, even offline.',
      },
    ],
  },
  {
    label: 'Menu and stock',
    shopLabel: 'Products and stock',
    sections: [
      {
        id: 'products',
        label: 'Products',
        area: 'stock',
        icon: Package,
        about: 'What a product can hold, and what staff may do with it.',
      },
      {
        id: 'units',
        label: 'Units',
        area: 'stock',
        icon: Ruler,
        about: 'What you count and sell in.',
      },
      {
        id: 'purchasing',
        label: 'Purchasing',
        area: 'buy',
        icon: Stamp,
        about: 'Whether purchase orders need approval, and who approves.',
      },
    ],
  },
  {
    label: 'You and this device',
    sections: [
      {
        id: 'alerts',
        label: 'Alerts',
        area: 'neutral',
        icon: Bell,
        about: 'What this phone tells you while the app is closed.',
      },
      {
        id: 'device',
        label: 'This device',
        area: 'neutral',
        icon: Smartphone,
        about: 'Installing the app and keeping its offline data safe.',
      },
    ],
  },
]

function countOf(n: number, one: string, many: string) {
  return `${n.toLocaleString('en-IN')} ${n === 1 ? one : many}`
}

/** A line under each section in the phone's list, only from what is already loaded. */
function useSummaries(): Record<SectionId, string> {
  const lookups = useLookups()
  const rates = lookups.taxRates.filter((t) => t.is_active).map((t) => t.name)
  const units = lookups.units.map((u) => u.name)
  const places = lookups.locations.filter((l) => l.is_active).length
  const some = (names: string[], fallback: string) =>
    names.length === 0
      ? fallback
      : names.length <= 3
        ? names.join(', ')
        : `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`
  return {
    business: 'Name, PAN, bill numbers, dates',
    locations: lookups.loaded
      ? countOf(places, 'place that holds stock', 'places that hold stock')
      : 'The places that hold your stock',
    team: 'People and what each may do',
    'day-report': 'One email after each day with sales',
    subscription: 'Your plan and payments',
    taxes: some(rates, 'VAT and other rates'),
    payments: 'The QR customers scan to pay',
    tables: 'Tables, kitchen tickets, QR codes',
    counter: 'Bill numbers when offline',
    products: 'Fields and rules for products',
    units: some(units, 'What you count and sell in'),
    purchasing: 'Approval of purchase orders',
    alerts: 'Approvals and a morning summary',
    device: 'Install and offline storage',
  }
}

export function SettingsScreen() {
  // The section lives in the URL so it survives reloads and can be linked to.
  const [params, setParams] = useSearchParams()
  const settings = useSettings()
  const { current } = useWorkspace()
  const role = useRole()
  const summaries = useSummaries()
  // Tables only in restaurant mode; Purchasing with purchase_control, for owners and admins.
  const restaurant = settings?.restaurant ?? false
  const purchasing = useFeature('purchase_control') && roleAtLeast(role, 'admin')
  // Alerts for staff and above, where push is set up (a build with a VAPID key).
  const { platform } = useSession()
  const alerts = Boolean(platform.push?.publicKey) && roleAtLeast(role, 'staff')
  const groups = GROUPS.map((g) => ({
    label: restaurant ? g.label : (g.shopLabel ?? g.label),
    sections: g.sections.filter(
      (s) =>
        (s.id !== 'tables' || restaurant) &&
        (s.id !== 'purchasing' || purchasing) &&
        (s.id !== 'alerts' || alerts) &&
        (s.id !== 'day-report' || roleAtLeast(role, 'manager')),
    ),
  }))
  const sections = groups.flatMap((g) => g.sections)
  // On a phone, Settings with no section in the address is the list itself; on a
  // wider screen the list sits beside the first section.
  const chosen = sections.find((s) => s.id === params.get('tab'))
  const section = chosen ?? sections[0]!
  const tab = section.id
  // From the bare list a section is a step forward (the phone's back returns to the
  // list); between sections it replaces, so back leaves Settings.
  const open = (id: SectionId) => setParams({ tab: id }, { replace: chosen !== undefined })
  const Icon = section.id === 'products' && restaurant ? BookOpenText : section.icon
  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle={
          settings ? (
            <>
              <span translate="no">{settings.business_name}</span>
              {current &&
                ` · you are ${role === 'owner' ? 'the' : role === 'admin' ? 'an' : 'a'} ${role}`}
            </>
          ) : undefined
        }
      />
      <div className="flex flex-col gap-5 md:flex-row md:items-start">
        <nav
          aria-label="Settings sections"
          className={cx(
            'shrink-0 md:sticky md:top-0 md:w-60 md:rounded-card md:bg-surface md:p-2 md:shadow-card',
            chosen && 'max-md:hidden',
          )}
        >
          {groups.map((g, gi) => (
            <div key={g.label}>
              <div
                className={cx('px-2.5 pb-1 text-section text-subtle', gi === 0 ? 'pt-1' : 'pt-3')}
              >
                {g.label}
              </div>
              <div className="flex flex-col gap-0.5 max-md:divide-y max-md:divide-soft max-md:rounded-card max-md:bg-surface max-md:shadow-card">
                {g.sections.map((s) => {
                  const SectionIcon = s.id === 'products' && restaurant ? BookOpenText : s.icon
                  const on = s.id === tab
                  return (
                    <button
                      key={s.id}
                      type="button"
                      aria-label={s.label}
                      aria-current={on ? 'page' : undefined}
                      onClick={() => open(s.id)}
                      className={cx(
                        'flex w-full items-center gap-2.5 px-2 py-1.5 text-left text-sm font-medium transition-colors max-md:min-h-12 max-md:gap-3 max-md:px-3 md:rounded-xl',
                        on
                          ? cx(
                              'max-md:text-muted md:font-semibold md:ring-1 md:ring-inset',
                              SECTION_ACTIVE[s.area],
                            )
                          : 'text-muted hover:text-ink md:hover:bg-soft',
                      )}
                    >
                      <IconTile area={s.area} size="sm">
                        <SectionIcon className="size-[17px]" />
                      </IconTile>
                      <span className="min-w-0 flex-1">
                        <span className="block max-md:font-semibold max-md:text-ink">
                          {s.label}
                        </span>
                        <span className="block truncate text-xs font-normal text-muted md:hidden">
                          {summaries[s.id]}
                        </span>
                      </span>
                      <ChevronRight className="size-4 text-subtle md:hidden" />
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </nav>
        <div className={cx('min-w-0 flex-1 md:max-w-[900px]', !chosen && 'max-md:hidden')}>
          <button
            type="button"
            onClick={() => setParams({}, { replace: true })}
            className="mb-2 flex min-h-9 items-center gap-1 text-sm font-semibold text-primary md:hidden"
          >
            <ChevronLeft className="size-4" />
            Settings
          </button>
          <div className="mb-3.5 flex items-center gap-3">
            <IconTile area={section.area}>
              <Icon className="size-5" />
            </IconTile>
            <div className="min-w-0">
              <h2 className="text-lg font-bold tracking-tight text-ink">{section.label}</h2>
              <p className="text-sm text-muted">{section.about}</p>
            </div>
          </div>
          {tab === 'business' && <BusinessSettings />}
          {tab === 'taxes' && <TaxSettings />}
          {tab === 'payments' && <PaymentSettings />}
          {tab === 'units' && <UnitSettings />}
          {tab === 'purchasing' && <PurchasingSettings />}
          {tab === 'products' && <CatalogSettings />}
          {tab === 'tables' && <TableSettings />}
          {tab === 'locations' && <LocationSettings />}
          {tab === 'counter' && <CounterSettings />}
          {tab === 'alerts' && <AlertSettings />}
          {tab === 'device' && <DeviceSettings />}
          {tab === 'team' && <TeamSettings />}
          {tab === 'day-report' && <DayReportSettings />}
          {tab === 'subscription' && <SubscriptionSettings />}
        </div>
      </div>
    </div>
  )
}

function useSave() {
  const repo = useRepo()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const save = async (ops: Parameters<typeof repo.commit>[0], message = 'Saved') => {
    setBusy(true)
    try {
      await repo.commit(ops)
      toast.success(message)
      return true
    } catch (error) {
      toast.error(errorMessage(error))
      return false
    } finally {
      setBusy(false)
    }
  }
  return { save, busy }
}

const DATE_DISPLAYS = [
  { value: 'both', label: 'Nepali and English' },
  { value: 'bs', label: 'Nepali (BS)' },
  { value: 'ad', label: 'English (AD)' },
] as const

type SettingsRow = NonNullable<ReturnType<typeof useSettings>>

function BusinessSettings() {
  const settings = useSettings()
  // Out here, so a settings sync (which remounts the form) never closes the logo dialog.
  const logoChoice = useLogoChoice()
  if (!settings) return <SkeletonForm />
  return (
    <BusinessForm
      key={settings.server_updated_at ?? settings.updated_at}
      settings={settings}
      logoChoice={logoChoice}
    />
  )
}

/** Emails the server sends the owners and admins (the daily low-stock alert). */
function EmailAlerts({ settings }: { settings: SettingsRow }) {
  const canEdit = useCan('admin')
  const { save, busy } = useSave()
  return (
    <SettingsPart
      title="Email alerts"
      about="Sent to the owners and admins of this business. Each email has a link to stop it for that person."
    >
      <Switch
        className="sm:col-span-2"
        label="Email low-stock alerts"
        description="Once a day, only when an item has newly run low (below its minimum stock). For everyone; admins change it."
        checked={settings.low_stock_email}
        disabled={!canEdit || busy}
        onChange={(e) =>
          void save([updateOp('tenant_settings', settings, { low_stock_email: e.target.checked })])
        }
      />
      {canEdit && <EmailToMe />}
    </SettingsPart>
  )
}

/**
 * This person's own low-stock email: the same choice as the link in the email
 * (email_pref_get / email_pref_set, online). Owners and admins only: nobody else gets it.
 */
function EmailToMe() {
  const { platform } = useSession()
  const { current } = useWorkspace()
  const online = useOnline()
  const toast = useToast()
  const tenantId = current?.tenant_id ?? ''
  const [pref, setPref] = useState<{ email: string | null; on: boolean } | null>(null)

  useEffect(() => {
    if (!tenantId) return
    let active = true
    void platform.supabase
      .rpc('email_pref_get', { p_tenant_id: tenantId, p_kind: 'low_stock' })
      .then(({ data, error }) => {
        if (!active) return
        if (error) toast.error(errorMessage(error))
        else setPref(data as { email: string | null; on: boolean })
      })
    return () => {
      active = false
    }
  }, [platform, tenantId, toast])

  // Shown at once; put back if the save fails.
  const turn = async (on: boolean) => {
    if (!pref) return
    const before = pref
    setPref({ ...pref, on })
    const { data, error } = await platform.supabase.rpc('email_pref_set', {
      p_tenant_id: tenantId,
      p_kind: 'low_stock',
      p_on: on,
    })
    if (error) {
      setPref(before)
      toast.error(errorMessage(error))
    } else setPref(data as { email: string | null; on: boolean })
  }

  const address = pref?.email ?? ''
  return (
    <div className="grid gap-2 sm:col-span-2">
      <Switch
        label="Send them to me"
        description={
          !online
            ? 'Needs the internet to change.'
            : pref === null
              ? 'Loading…'
              : pref.on
                ? address
                : `${address} · Off: you stopped them with the link in an email. Turn on to get them again.`
        }
        checked={pref?.on ?? false}
        disabled={!online || pref === null || !pref.email}
        onChange={(e) => void turn(e.target.checked)}
      />
      <p className="text-[12.5px] text-muted">
        Your own choice. The morning summary on your phone is in Settings → Alerts.
      </p>
    </div>
  )
}

function businessFormOf(settings: SettingsRow) {
  return {
    business_name: settings.business_name,
    legal_name: settings.legal_name ?? '',
    pan_vat_number: settings.pan_vat_number ?? '',
    tax_registration: settings.tax_registration,
    address: settings.address ?? '',
    phone: settings.phone ?? '',
    email: settings.email ?? '',
    invoice_prefix: settings.invoice_prefix,
    credit_note_prefix: settings.credit_note_prefix,
    quotation_prefix: settings.quotation_prefix,
    sales_order_prefix: settings.sales_order_prefix,
    purchase_order_prefix: settings.purchase_order_prefix,
    quote_valid_days: String(settings.quote_valid_days),
    quote_terms: settings.quote_terms ?? '',
    default_tax_rate_id: settings.default_tax_rate_id ?? '',
    prices_include_tax: settings.prices_include_tax,
    invoice_footer: settings.invoice_footer ?? '',
    // The business's setting: this device may show dates another way (the account menu).
    date_display: businessDateDisplay(settings),
    fiscal_calendar: settings.fiscal_calendar,
  }
}

/** What the business sells and offers, kept apart from the columns the form spreads. */
function kindsFormOf(settings: SettingsRow) {
  return {
    kinds: businessKinds(settings),
    services: providesServices(settings),
    ownFields: Array.isArray(settings.field_groups),
  }
}

/** The settings columns for the kinds as edited (docs/plans/business-kinds.md). */
function kindsChange(settings: SettingsRow, k: ReturnType<typeof kindsFormOf>) {
  const [main = 'general', ...others] = k.kinds
  return {
    business_type: main,
    business_kinds: others.length ? others : null,
    // Kept as it was (often "the kind's default") unless the switch or the main kind changed.
    provides_services:
      k.services === providesServices(settings) && main === settings.business_type
        ? settings.provides_services
        : k.services,
    field_groups: k.ownFields ? settings.field_groups : null,
    // Choosing a restaurant turns on restaurant mode, as it does at sign-up.
    restaurant: main === 'restaurant' ? true : settings.restaurant,
  }
}

function BusinessForm({ settings, logoChoice }: { settings: SettingsRow; logoChoice: LogoChoice }) {
  const canEdit = useCan('admin')
  const repo = useRepo()
  const lookups = useLookups()
  const { save, busy } = useSave()
  const [form, setForm] = useState(() => businessFormOf(settings))
  const [kindsForm, setKindsForm] = useState(() => kindsFormOf(settings))
  // The fiscal year is part of every invoice number: fixed once there is a bill
  // (the server refuses the change too: fiscal_locked).
  const fiscalLocked = useLive(
    async (repo) => {
      const [finalized, cancelled] = await Promise.all([
        repo.invoices.list({ status: 'finalized', limit: 1 }),
        repo.invoices.list({ status: 'cancelled', limit: 1 }),
      ])
      return finalized.total + cancelled.total > 0
    },
    [],
    ['sales_invoices'],
  ).data
  // A PAN-only shop should not charge VAT: count what still does, to warn (nothing is changed).
  const productTaxIds = useLive(
    async (repo) =>
      (await repo.list('products', { limit: 1_000_000 })).rows
        .filter((p) => p.is_active && p.kind !== 'group')
        .map((p) => p.tax_rate_id),
    [],
    ['products'],
  ).data
  const charged = (id: string | null | undefined) => (lookups.taxRate(id)?.rate_bp ?? 0) > 0
  const vatProducts = productTaxIds?.filter(charged).length ?? 0
  const isVat = form.tax_registration === 'vat'
  // A shop that registers for VAT starts charging it on new products. The rates are
  // read now: the lookups may still be loading right after the page opens.
  const chooseRegistration = async (next: 'pan' | 'vat') => {
    setForm((f) => ({ ...f, tax_registration: next }))
    if (next !== 'vat') return
    const rates = (
      await repo.list('tax_rates', { orderBy: { column: 'rate_bp', direction: 'desc' } })
    ).rows
    const vat = rates.find((t) => t.is_active && t.rate_bp > 0)
    if (vat) setForm((f) => (f.default_tax_rate_id ? f : { ...f, default_tax_rate_id: vat.id }))
  }
  const today = todayIso()
  const set = (key: keyof typeof form, value: string | boolean) =>
    setForm((f) => ({ ...f, [key]: value }))
  const text = (key: keyof typeof form, label: string, hint?: string, className?: string) => (
    <Field label={label} hint={hint} className={className}>
      {(id) => (
        <Input
          id={id}
          value={form[key] as string}
          disabled={!canEdit}
          onChange={(e) => set(key, e.target.value)}
        />
      )}
    </Field>
  )
  // Any edit: the browser asks before a reload, and Save stays in view.
  const [dirty, setDirty] = useState(false)
  useUnsavedWarning(dirty)
  const discard = () => {
    setForm(businessFormOf(settings))
    setKindsForm(kindsFormOf(settings))
    setDirty(false)
  }
  const editKinds = (change: Partial<ReturnType<typeof kindsFormOf>>) => {
    setKindsForm((k) => ({ ...k, ...change }))
    setDirty(true)
  }
  return (
    <Card className="p-0">
      {!canEdit && (
        <p className="px-4 pt-4 text-sm text-muted sm:px-6">
          Only admins can change business settings.
        </p>
      )}
      <div
        onChangeCapture={(e) => {
          // The logo saves on its own; only the form's fields wait for Save.
          if (!(e.target as HTMLElement).closest('[data-own-save]')) setDirty(true)
        }}
      >
        <SettingsPart
          title="Business details"
          about="Printed at the top of every bill and receipt."
        >
          {text('business_name', 'Business name (on invoices)')}
          {text('legal_name', 'Legal name')}
          {text('phone', 'Phone')}
          {text('email', 'Email')}
          {text('address', 'Address', undefined, 'sm:col-span-2')}
        </SettingsPart>
        <KindsParts
          kinds={kindsForm.kinds}
          savedKinds={businessKinds(settings)}
          onKinds={(kinds) => editKinds({ kinds })}
          services={kindsForm.services}
          onServices={(services) => editKinds({ services })}
          ownFields={kindsForm.ownFields}
          onUseTheirFields={() => editKinds({ ownFields: false })}
          canEdit={canEdit}
        />
        <LogoPart settings={settings} save={save} busy={busy} choice={logoChoice} />
        <SettingsPart title="Tax" about="Decides the title of a bill and whether VAT shows on it.">
          <Field
            label="Tax registration"
            hint={isVat ? 'Bills are titled “Tax invoice”.' : 'Bills are titled “Invoice”.'}
          >
            {(id) => (
              <Select
                id={id}
                value={form.tax_registration}
                disabled={!canEdit}
                onChange={(e) => void chooseRegistration(e.target.value as 'pan' | 'vat')}
              >
                <option value="pan">PAN only</option>
                <option value="vat">VAT registered</option>
              </Select>
            )}
          </Field>
          {text('pan_vat_number', taxNumberLabel(form.tax_registration) + ' (on invoices)')}
          {isVat && (
            <p className="rounded-xl bg-info-light p-3 text-sm sm:col-span-2">
              <span className="font-medium">
                This app is not yet IRD-approved billing software.
              </span>{' '}
              Reporting bills to the IRD and marking reprints as copies are planned. Until then,
              keep issuing your official VAT bill from your IRD-approved bill book or software.
            </p>
          )}
          {!isVat && (charged(form.default_tax_rate_id) || vatProducts > 0) && (
            <p className="rounded-xl bg-warning-light p-3 text-sm text-warning sm:col-span-2">
              A PAN-only shop should not charge VAT.{' '}
              {vatProducts > 0 &&
                `${vatProducts.toLocaleString('en-IN')} product${vatProducts === 1 ? ' still has' : 's still have'} a VAT rate. `}
              {charged(form.default_tax_rate_id) &&
                'New products get VAT by default (change it below). '}
              {vatProducts > 0 && (
                <Link to="/products" className="font-medium underline">
                  Change them with Products → Select
                </Link>
              )}
            </p>
          )}
          <Field label="Default VAT for new products">
            {(id) => (
              <Select
                id={id}
                value={form.default_tax_rate_id}
                disabled={!canEdit}
                onChange={(e) => set('default_tax_rate_id', e.target.value)}
              >
                <option value="">No VAT</option>
                {lookups.taxRates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Switch
            className="sm:col-span-2"
            label="New products’ prices include VAT"
            description="The price you type is what the customer pays."
            checked={form.prices_include_tax}
            disabled={!canEdit}
            onChange={(e) => set('prices_include_tax', e.target.checked)}
          />
        </SettingsPart>
        <SettingsPart
          title="Bill numbers"
          about="The letters before each number, and what is printed under a bill."
        >
          {text('invoice_prefix', 'Invoice prefix', 'Capital letters/digits, e.g. INV')}
          {text('credit_note_prefix', 'Credit note prefix', 'e.g. CN')}
          {text('purchase_order_prefix', 'Purchase order prefix', 'e.g. PO')}
          {!settings.restaurant && (
            <>
              {text('quotation_prefix', 'Quotation prefix', 'e.g. QT')}
              {text('sales_order_prefix', 'Order prefix', 'e.g. SO')}
              {text('quote_valid_days', 'Quotations are valid for (days)', '1 to 365')}
              <Field
                label="Quotation terms"
                hint="Printed under every quotation that has no terms of its own."
                className="sm:col-span-2"
              >
                {(id) => (
                  <Textarea
                    id={id}
                    value={form.quote_terms}
                    disabled={!canEdit}
                    onChange={(e) => set('quote_terms', e.target.value)}
                    placeholder="e.g. Delivery within 7 days. 50% advance with the order."
                  />
                )}
              </Field>
            </>
          )}
          <Field label="Invoice footer" className="sm:col-span-2">
            {(id) => (
              <Textarea
                id={id}
                value={form.invoice_footer}
                disabled={!canEdit}
                onChange={(e) => set('invoice_footer', e.target.value)}
                placeholder="e.g. Goods once sold are not returnable after 7 days."
              />
            )}
          </Field>
        </SettingsPart>
        <SettingsPart
          title="Dates"
          about="For the whole business, and on every printed bill. Anyone can read dates another way on their own device: the account menu at the top right has a Dates switch."
        >
          <fieldset className="sm:col-span-2">
            <legend className="mb-2 text-sm font-medium text-muted">Show dates in</legend>
            <div className="grid gap-2">
              {DATE_DISPLAYS.map((o) => (
                <label
                  key={o.value}
                  className={cx(
                    'flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 text-sm',
                    form.date_display === o.value
                      ? 'border-primary bg-primary-light/40'
                      : 'border-border',
                    !canEdit && 'cursor-default opacity-70',
                  )}
                >
                  <input
                    type="radio"
                    name="date-display"
                    className="size-4 shrink-0 accent-primary"
                    checked={form.date_display === o.value}
                    disabled={!canEdit}
                    onChange={() => set('date_display', o.value)}
                  />
                  <span className="flex min-w-0 flex-1 flex-wrap justify-between gap-x-3">
                    <span className="font-medium">{o.label}</span>
                    <span className="text-muted">
                      {dateLabel(today, { date_display: o.value })}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted">
              For every screen and report. Printed bills and shared invoice links always show the
              Nepali date too.
            </p>
          </fieldset>
          <Field
            label="Fiscal year"
            className="sm:col-span-2"
            hint={
              fiscalLocked
                ? 'Fixed after the first bill: the fiscal year is part of every invoice number.'
                : 'Used in invoice numbers and reports. Fixed once you make the first bill.'
            }
          >
            {(id) => (
              <Select
                id={id}
                value={form.fiscal_calendar}
                disabled={!canEdit || fiscalLocked !== false}
                onChange={(e) => set('fiscal_calendar', e.target.value)}
              >
                <option value="bs">Nepali (1 Shrawan to end of Asar)</option>
                <option value="ad">English (1 January to 31 December)</option>
              </Select>
            )}
          </Field>
        </SettingsPart>
      </div>
      {/* Saves at once, so it sits outside the part that marks "changes to save". */}
      <EmailAlerts settings={settings} />
      {canEdit && (
        <SaveBar
          dirty={dirty}
          busy={busy}
          disabled={!form.business_name.trim()}
          onDiscard={discard}
          onSave={() =>
            void save([
              updateOp('tenant_settings', settings, {
                ...form,
                ...kindsChange(settings, kindsForm),
                business_name: form.business_name.trim(),
                legal_name: form.legal_name.trim() || null,
                pan_vat_number: form.pan_vat_number.trim() || null,
                address: form.address.trim() || null,
                phone: form.phone.trim() || null,
                email: form.email.trim() || null,
                invoice_prefix: form.invoice_prefix.trim().toUpperCase(),
                credit_note_prefix: form.credit_note_prefix.trim().toUpperCase(),
                quotation_prefix: form.quotation_prefix.trim().toUpperCase(),
                sales_order_prefix: form.sales_order_prefix.trim().toUpperCase(),
                purchase_order_prefix: form.purchase_order_prefix.trim().toUpperCase(),
                quote_valid_days: Math.min(
                  365,
                  Math.max(1, Math.round(Number(form.quote_valid_days)) || 15),
                ),
                quote_terms: form.quote_terms.trim() || null,
                default_tax_rate_id: form.default_tax_rate_id || null,
                invoice_footer: form.invoice_footer.trim() || null,
                ...(form.fiscal_calendar === settings.fiscal_calendar
                  ? {}
                  : form.fiscal_calendar === 'ad'
                    ? { fiscal_year_start_month: 1, fiscal_year_start_day: 1 }
                    : { fiscal_year_start_month: 4, fiscal_year_start_day: 1 }),
              }),
            ])
          }
        />
      )}
    </Card>
  )
}

function TaxSettings() {
  const lookups = useLookups()
  const canEdit = useCan('admin')
  const { current } = useWorkspace()
  const { save, busy } = useSave()
  const [name, setName] = useState('')
  const [rate, setRate] = useState('')
  const rateBp = readAmountText(rate, 2)?.value ?? null
  return (
    <Card className="p-0">
      {!lookups.loaded ? (
        <SkeletonTable rows={2} cols={3} />
      ) : (
        <Table>
          <thead>
            <tr>
              <TH>Name</TH>
              <TH align="right">Rate</TH>
              <TH>Status</TH>
              <TH />
            </tr>
          </thead>
          <tbody>
            {lookups.taxRates.map((t) => (
              <tr key={t.id}>
                <TD className="font-medium">{t.name}</TD>
                <TD align="right">{t.rate_bp / 100}%</TD>
                <TD>
                  {t.is_active ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge>}
                </TD>
                <TD align="right">
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        void save([updateOp('tax_rates', t, { is_active: !t.is_active })])
                      }
                    >
                      {t.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                  )}
                </TD>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {canEdit && (
        <div className="flex flex-wrap items-end gap-3 border-t border-border p-4">
          <Field label="Name" className="flex-1">
            {(id) => (
              <Input
                id={id}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Excise 5%"
              />
            )}
          </Field>
          <Field label="Rate %" className="w-28">
            {(id) => (
              <Input
                id={id}
                inputMode="decimal"
                value={rate}
                onChange={(e) => setRate(keepTyped(e.target.value, rate))}
                onBlur={() => setRate((r) => readAmountText(r, 2)?.tidy ?? r)}
              />
            )}
          </Field>
          <Button
            icon={<Plus className="size-4" />}
            loading={busy}
            disabled={!name.trim() || rateBp === null || rateBp > 10000}
            onClick={() =>
              void save([
                insertOp(
                  'tax_rates',
                  newTaxRate(
                    { tenantId: current!.tenant_id },
                    { name: name.trim(), rate_bp: rateBp! },
                  ),
                ),
              ]).then((ok) => ok && (setName(''), setRate('')))
            }
          >
            Add
          </Button>
        </div>
      )}
    </Card>
  )
}

function LocationSettings() {
  const lookups = useLookups()
  const canAdd = useCan('manager')
  const canToggle = useCan('admin')
  const { current } = useWorkspace()
  const { save, busy } = useSave()
  const [name, setName] = useState('')
  const [type, setType] = useState<'store' | 'warehouse'>('store')
  return (
    <Card className="p-0">
      {!lookups.loaded ? (
        <SkeletonTable rows={2} cols={3} />
      ) : (
        <Table>
          <thead>
            <tr>
              <TH>Name</TH>
              <TH>Type</TH>
              <TH>Status</TH>
              <TH />
            </tr>
          </thead>
          <tbody>
            {lookups.locations.map((l) => (
              <tr key={l.id}>
                <TD className="font-medium">{l.name}</TD>
                <TD className="capitalize text-muted">{l.type}</TD>
                <TD>
                  {l.is_active ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge>}
                </TD>
                <TD align="right">
                  {canToggle && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        void save([updateOp('locations', l, { is_active: !l.is_active })])
                      }
                    >
                      {l.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                  )}
                </TD>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {canAdd && (
        <div className="flex flex-wrap items-end gap-3 border-t border-border p-4">
          <Field label="New location" className="flex-1">
            {(id) => (
              <Input
                id={id}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Godown"
              />
            )}
          </Field>
          <Field label="Type" className="w-36">
            {(id) => (
              <Select
                id={id}
                value={type}
                onChange={(e) => setType(e.target.value as 'store' | 'warehouse')}
              >
                <option value="store">Store</option>
                <option value="warehouse">Warehouse</option>
              </Select>
            )}
          </Field>
          <Button
            icon={<Plus className="size-4" />}
            loading={busy}
            disabled={!name.trim()}
            onClick={() =>
              void save([
                insertOp(
                  'locations',
                  newLocation({ tenantId: current!.tenant_id }, { name: name.trim(), type }),
                ),
              ]).then((ok) => ok && setName(''))
            }
          >
            Add
          </Button>
        </div>
      )}
    </Card>
  )
}

function CounterSettings() {
  const repo = useRepo()
  const { engine } = useStore()
  const canRegister = useCan('manager')
  const online = useOnline()
  const toast = useToast()
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const counter = useLive((r) => r.counters.current(), [], ['billing_counters']).data
  const counters = useLive(
    (r) => r.list('billing_counters', { orderBy: { column: 'code' }, limit: 100 }),
    [],
    ['billing_counters'],
  ).data?.rows
  const all = counters ?? []
  void repo

  const register = async () => {
    setBusy(true)
    try {
      await engine.registerCounter(name.trim() || undefined)
      toast.success('This device is now a billing counter')
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-primary-light p-2.5 text-primary">
            <MonitorSmartphone className="size-5" />
          </div>
          <div className="flex-1 text-sm">
            <h2 className="font-semibold">This device</h2>
            {counter ? (
              <p className="mt-1">
                Counter <b>{counter.code}</b> ({counter.name}). Invoices made here are numbered{' '}
                <code>…-{counter.code}-…</code> and get their number immediately, even offline.
              </p>
            ) : (
              <p className="mt-1 text-muted">
                Not registered. Invoices made here get their number from the server when they sync
                (shown as “number on sync”). Register this device to number invoices offline.
              </p>
            )}
          </div>
        </div>
        {!counter && canRegister && (
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <Field label="Counter name (optional)" className="flex-1">
              {(id) => (
                <Input
                  id={id}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Front desk"
                />
              )}
            </Field>
            <Button loading={busy} disabled={!online} onClick={() => void register()}>
              Register this device
            </Button>
          </div>
        )}
        {!counter && !canRegister && (
          <p className="mt-3 text-sm text-muted">A manager needs to register this device.</p>
        )}
      </Card>
      <Card className="p-0">
        {counters === undefined ? (
          <SkeletonTable rows={2} cols={3} />
        ) : (
          <Table>
            <thead>
              <tr>
                <TH>Code</TH>
                <TH>Name</TH>
                <TH>Type</TH>
                <TH>Status</TH>
              </tr>
            </thead>
            <tbody>
              {all.map((c) => (
                <tr key={c.id}>
                  <TD className="font-medium">{c.code}</TD>
                  <TD>{c.name}</TD>
                  <TD className="text-muted">{c.kind === 'web' ? 'Server-numbered' : 'Device'}</TD>
                  <TD>
                    {c.is_active ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge>}
                  </TD>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  )
}

interface Member {
  user_id: string
  role: MemberRole
  email: string | null
  full_name: string | null
}

const ROLES: MemberRole[] = ['viewer', 'waiter', 'staff', 'manager', 'admin', 'owner']

/** The two letters in a member's circle: from the name, else the email. */
function initials(name: string) {
  const words = name
    .split('@')[0]!
    .split(/[\s._-]+/)
    .filter(Boolean)
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || '?'
}

function TeamSettings() {
  const settings = useSettings()
  const { platform } = useSession()
  const me = useUser()
  const { current } = useWorkspace()
  const myRole = useRole()
  const canAdmin = useCan('admin')
  const online = useOnline()
  const toast = useToast()
  const [members, setMembers] = useState<Member[] | null>(null)
  const [invites, setInvites] = useState<
    { id: string; email: string; role: string; expires_at: string }[]
  >([])
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Exclude<MemberRole, 'owner'>>('staff')
  const [busy, setBusy] = useState(false)
  const [link, setLink] = useState<string | null>(null)
  const [removing, setRemoving] = useState<Member | null>(null)
  const tenantId = current!.tenant_id
  const supabase = platform.supabase

  const load = useCallback(async () => {
    const { data: rows, error } = await supabase
      .from('memberships')
      .select('user_id, role')
      .eq('tenant_id', tenantId)
    if (error) throw error
    const ids = (rows ?? []).map((r) => r.user_id as string)
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, email, full_name')
      .in('id', ids)
    const byId = new Map((profiles ?? []).map((p) => [p.id as string, p]))
    const list = (rows ?? []).map((r) => ({
      user_id: r.user_id as string,
      role: r.role as MemberRole,
      email: (byId.get(r.user_id as string)?.email as string) ?? null,
      full_name: (byId.get(r.user_id as string)?.full_name as string) ?? null,
    }))
    list.sort((a, b) => ROLES.indexOf(b.role) - ROLES.indexOf(a.role))
    let pending: typeof invites = []
    if (canAdmin) {
      const { data } = await supabase
        .from('invites')
        .select('id, email, role, expires_at')
        .eq('tenant_id', tenantId)
        .is('accepted_at', null)
        .is('revoked_at', null)
      pending = (data ?? []) as typeof invites
    }
    return { list, pending }
  }, [supabase, tenantId, canAdmin])

  const refresh = useCallback(() => {
    load().then(
      ({ list, pending }) => {
        setMembers(list)
        setInvites(pending)
      },
      (error: unknown) => toast.error(errorMessage(error)),
    )
  }, [load, toast])

  useEffect(() => {
    if (!online) return
    let active = true
    load().then(
      ({ list, pending }) => {
        if (!active) return
        setMembers(list)
        setInvites(pending)
      },
      () => {},
    )
    return () => {
      active = false
    }
  }, [load, online])

  if (!online)
    return (
      <Card>
        <EmptyState title="Team management needs an internet connection" />
      </Card>
    )
  if (!members)
    return (
      <Card className="p-0">
        <SkeletonTable rows={3} cols={3} label="Loading team" />
      </Card>
    )

  const api = tenantApi(supabase)
  const act = async (fn: () => Promise<unknown>, message: string) => {
    setBusy(true)
    try {
      await fn()
      toast.success(message)
      refresh()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Card className="p-0">
        {canAdmin && (
          <SettingsPart
            title="Invite someone"
            about="They get an email with a link. Staff can bill and manage stock; managers can also cancel invoices and add locations; admins manage settings and the team."
          >
            <Field label="Email">
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>
            <Field label="Role">
              {(id) => (
                <Select
                  id={id}
                  className="capitalize"
                  value={role}
                  onChange={(e) => setRole(e.target.value as typeof role)}
                >
                  {ROLES.filter(
                    (r) =>
                      r !== 'owner' &&
                      ROLES.indexOf(r) <= ROLES.indexOf(myRole) &&
                      (r !== 'waiter' || settings?.restaurant),
                  ).map((r) => (
                    <option key={r} value={r} className="capitalize">
                      {r}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <div className="sm:col-span-2">
              <Button
                icon={<UserPlus className="size-4" />}
                loading={busy}
                disabled={!email.includes('@')}
                onClick={() =>
                  void act(async () => {
                    const res = await createApi(platform.apiUrl, supabase).invite(
                      tenantId,
                      email.trim(),
                      role,
                    )
                    setLink(res.link ?? null)
                    setEmail('')
                  }, 'Invite sent')
                }
              >
                Send invite
              </Button>
            </div>
            {link && (
              <div className="flex items-center gap-2 rounded-xl bg-info-light p-3 text-sm sm:col-span-2">
                <span className="break-all">Email isn’t set up, so share this link: {link}</span>
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<Copy className="size-3.5" />}
                  onClick={() => void navigator.clipboard?.writeText(link)}
                >
                  Copy
                </Button>
              </div>
            )}
          </SettingsPart>
        )}
        <section className="border-t border-border first:border-t-0">
          <div className="flex items-baseline gap-2 px-4 pt-4 pb-2 sm:px-6 sm:pt-5">
            <h3 className="font-semibold text-ink">Members</h3>
            <span className="text-sm text-muted">
              {countOf(members.length, 'person', 'people')}
            </span>
          </div>
          <Table stack>
            <thead>
              <tr>
                <TH className="sm:pl-6">Member</TH>
                <TH>Role</TH>
                <TH />
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.user_id}>
                  <TD className="stack-r1 sm:pl-6">
                    <div className="flex items-center gap-3">
                      <span
                        aria-hidden="true"
                        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-people-light text-xs font-bold text-people uppercase"
                      >
                        {initials(m.full_name || m.email || '')}
                      </span>
                      <div className="min-w-0">
                        <div className="font-medium">
                          {m.full_name || m.email}
                          {m.user_id === me.id && (
                            <Badge tone="primary" className="ml-2">
                              You
                            </Badge>
                          )}
                        </div>
                        {m.full_name && <div className="text-xs text-muted">{m.email}</div>}
                      </div>
                    </div>
                  </TD>
                  <TD className="stack-main stack-r2 max-sm:mt-2 max-sm:pl-12">
                    {canAdmin &&
                    m.user_id !== me.id &&
                    ROLES.indexOf(m.role) <= ROLES.indexOf(myRole) ? (
                      <Select
                        aria-label={`Role of ${m.email}`}
                        className="w-32 capitalize"
                        value={m.role}
                        onChange={(e) =>
                          void act(
                            () =>
                              api.setMemberRole(tenantId, m.user_id, e.target.value as MemberRole),
                            'Role updated',
                          )
                        }
                      >
                        {ROLES.filter(
                          (r) =>
                            ROLES.indexOf(r) <= ROLES.indexOf(myRole) &&
                            (r !== 'waiter' || settings?.restaurant || m.role === 'waiter'),
                        ).map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <span className="capitalize">{m.role}</span>
                    )}
                  </TD>
                  <TD align="right" className="stack-end stack-r2 sm:pr-6">
                    {(m.user_id === me.id ||
                      (canAdmin && ROLES.indexOf(m.role) <= ROLES.indexOf(myRole))) && (
                      <Button size="sm" variant="ghost" onClick={() => setRemoving(m)}>
                        {m.user_id === me.id ? 'Leave' : 'Remove'}
                      </Button>
                    )}
                  </TD>
                </tr>
              ))}
            </tbody>
          </Table>
        </section>
        {invites.length > 0 && (
          <section className="border-t border-border p-4 sm:p-6">
            <h3 className="mb-2 font-semibold text-ink">Pending invites</h3>
            <ul className="grid gap-2">
              {invites.map((i) => (
                <li
                  key={i.id}
                  className="flex items-center gap-3 rounded-xl border border-dashed border-border px-3 py-2.5"
                >
                  <IconTile area="neutral" className="rounded-full">
                    <Mail className="size-[18px]" />
                  </IconTile>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{i.email}</div>
                    <div className="text-xs text-muted">
                      Invited as <span className="capitalize">{i.role}</span> · expires{' '}
                      {dateLabel(i.expires_at, settings)}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void act(() => api.revokeInvite(i.id), 'Invite revoked')}
                  >
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </Card>
      <ConfirmDialog
        open={removing !== null}
        title={removing?.user_id === me.id ? 'Leave this business?' : `Remove ${removing?.email}?`}
        message="They lose access immediately on their next sync."
        confirmLabel={removing?.user_id === me.id ? 'Leave' : 'Remove'}
        danger
        busy={busy}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          void act(() => api.removeMember(tenantId, removing!.user_id), 'Removed').then(() =>
            setRemoving(null),
          )
        }
      />
    </>
  )
}
